/* iter55f83 — LoginForumStream.
 *
 * "The Margin · Forum" preview section rendered at the bottom of the
 * /login page. Fetches `/api/public/community/activity` for real
 * writers-in-progress posts; falls back to a hand-seeded fixture when
 * the endpoint is empty or offline. Signed-in visitors get an inline
 * composer that posts under the "Introductions" topic.
 *
 * All original testids are preserved:
 *   `login-forum-stream`
 *   `login-forum-live-badge`
 *   `login-forum-post-<id>`
 *   `login-forum-composer`
 *   `login-forum-composer-wip`
 *   `login-forum-composer-input`
 *   `login-forum-composer-submit`
 *   `login-nav-bookstore`
 *   `login-nav-home`
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Feather, Loader2, MessageSquare, Send, BookOpen, PenTool } from 'lucide-react';
import { P, FONTS, GRAIN_URL } from '@/components/landing/scriptorium/palette';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/* Mock forum posts — used as the fallback when the live feed at
 * `/api/public/community/activity` returns nothing (fresh deploy,
 * forum quiet, or endpoint offline). */
const FORUM_POSTS = [
  { id: 'f1', author: 'Aster Vale',   wip: 'The Lighthouse Keeper', body: 'Struck the third chapter — the keeper won\'t leave the island. Anyone else have a character quietly veto a plot beat?', ago: '4m' },
  { id: 'f2', author: 'M. Grieve',    wip: 'Salt and Hollow',       body: 'Any recommended reads on grief structure in short novels? I keep landing on Marilynne Robinson.', ago: '22m' },
  { id: 'f3', author: 'Eleni Osunde', wip: 'The Cartographer',      body: 'Pro tip: dropping the character bible into the Drafting-mode prompt keeps voice consistency across long chapters.', ago: '1h' },
  { id: 'f4', author: 'Rasim Ivanov', wip: 'Fifth Voyage',          body: 'Finished my first pass at 68k words. The wordcount badge went off and I actually cried a little.', ago: '3h' },
];

function ago(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60)    return `${Math.floor(s)}s`;
  if (s < 3600)  return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function mapActivityPosts(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.slice(0, 4).map((r, i) => ({
    id:     `live-${i}`,
    author: r.first_name || 'Anon',
    wip:    (r.topic || 'Community')
              .replace(/[-_]+/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase()),
    body:   r.snippet || '',
    ago:    ago(r.at),
  }));
}

export default function LoginForumStream({ user, sectionId = 'login-forum-stream' }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState(FORUM_POSTS);
  const [isLive, setIsLive] = useState(false);
  const [note, setNote] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [wipTitle, setWipTitle] = useState('');

  /* Working-title auto-tag: fetch the signed-in user's most-recently
   * updated The Loom project so the composer chip reads like real
   * writers-in-progress, not context-free notes. */
  useEffect(() => {
    if (!user) { setWipTitle(''); return; }
    let cancelled = false;
    axios.get(`${API}/projects`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const first = Array.isArray(r.data) ? r.data[0] : null;
        setWipTitle((first?.title || '').trim());
      })
      .catch(() => { /* silent — composer still works untagged */ });
    return () => { cancelled = true; };
  }, [user]);

  const refreshActivity = useCallback(() => {
    let cancelled = false;
    axios.get(`${API}/public/community/activity`, { withCredentials: false })
      .then((r) => {
        if (cancelled) return;
        const mapped = mapActivityPosts(r.data?.recent_posts);
        if (mapped.length > 0) {
          setPosts(mapped);
          setIsLive(true);
        }
      })
      .catch(() => { /* silent — the seed fallback already renders */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = refreshActivity();
    return cleanup;
  }, [refreshActivity]);

  const postNote = async (e) => {
    e.preventDefault();
    const body = note.trim();
    if (body.length < 4 || postingNote) return;
    setPostingNote(true);
    try {
      const decorated = wipTitle ? t('login.forum.decorated', { wipTitle, body }) : body;
      const title = decorated.length > 60 ? `${decorated.slice(0, 57).trim()}…` : decorated;
      await axios.post(
        `${API}/forum/threads`,
        { topic_slug: 'introductions', title, body: decorated },
        { withCredentials: true },
      );
      toast.success(t('login.forum.postedSuccess'));
      setNote('');
      refreshActivity();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('login.forum.postError'));
    } finally {
      setPostingNote(false);
    }
  };

  return (
    <section
      id={sectionId}
      data-testid="login-forum-stream"
      className="mt-12"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div
            className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: P.oxblood, fontFamily: FONTS.body }}
          >
            {t('login.forum.title')}
            {isLive && (
              <span
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px]"
                data-testid="login-forum-live-badge"
                style={{
                  borderColor:     `${P.verdant}66`,
                  backgroundColor: `${P.verdant}18`,
                  color:           P.verdant,
                }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                  style={{ backgroundColor: P.verdant }}
                  aria-hidden
                />
                {t('login.forum.live')}
              </span>
            )}
          </div>
          <h3
            className="mt-1 text-2xl"
            style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
          >
            {t('login.forum.heading')}
          </h3>
        </div>
        <MessageSquare className="h-5 w-5" style={{ color: P.oxblood }} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {posts.map((post) => (
          <div
            key={post.id}
            data-testid={`login-forum-post-${post.id}`}
            className="relative rounded-md border p-4"
            style={{
              backgroundColor: P.paperDeep,
              borderColor:     `${P.gold}88`,
              boxShadow:       `0 6px 14px -8px ${P.leather}55`,
            }}
          >
            <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: GRAIN_URL }} />
            <div className="relative">
              <div className="flex items-baseline justify-between">
                <div>
                  <div
                    className="text-[13px] leading-tight"
                    style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.oxblood }}
                  >
                    {post.author}
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.body }}
                  >
                    {t('login.forum.workingOn')} <span style={{ color: P.verdant, fontWeight: 500 }}>{post.wip}</span>
                  </div>
                </div>
                <span
                  className="text-[10.5px]"
                  style={{ color: P.inkSoft, fontFamily: FONTS.type }}
                >
                  {t('login.forum.agoTemplate', { time: post.ago })}
                </span>
              </div>
              <p
                className="mt-2 text-[13px] leading-relaxed"
                style={{ color: P.ink, fontFamily: FONTS.body }}
              >
                {post.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      {user && (
        <form
          onSubmit={postNote}
          data-testid="login-forum-composer"
          className="relative mt-4 rounded-md border p-4"
          style={{
            backgroundColor: P.paperDeep,
            borderColor:     P.gold,
            boxShadow:       `0 10px 24px -18px ${P.leather}66`,
          }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: GRAIN_URL }} />
          <div className="relative">
            <div
              className="mb-2 flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              <Feather className="h-3 w-3" />
              {t('login.forum.dropNote', { name: user.name || t('login.forum.yourself') })}
              {wipTitle && (
                <span
                  data-testid="login-forum-composer-wip"
                  className="ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] normal-case tracking-normal"
                  style={{
                    borderColor:     `${P.verdant}66`,
                    backgroundColor: `${P.verdant}18`,
                    color:           P.verdant,
                    fontFamily:      FONTS.body,
                  }}
                  title="Auto-tagged from your most recently updated The Loom project"
                >
                  {t('login.forum.workingOnWip')}&nbsp;
                  <span style={{ fontFamily: FONTS.display, fontWeight: 600 }}>
                    {wipTitle}
                  </span>
                </span>
              )}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={postingNote}
              maxLength={480}
              rows={2}
              placeholder={t('login.forum.composerPlaceholder')}
              data-testid="login-forum-composer-input"
              className="w-full resize-none rounded-md border px-3 py-2 text-[13px] outline-none disabled:opacity-60"
              style={{
                backgroundColor: P.paper,
                borderColor:     `${P.gold}88`,
                color:           P.ink,
                fontFamily:      FONTS.body,
              }}
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[10.5px]" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
                {t('login.forum.counterHint', { length: note.trim().length })}
              </span>
              <button
                type="submit"
                disabled={postingNote || note.trim().length < 4}
                data-testid="login-forum-composer-submit"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold disabled:opacity-60"
                style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
              >
                {postingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {t('login.forum.postButton')}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4" style={{ borderColor: `${P.gold}55` }}>
        <div className="text-[11px]" style={{ color: P.inkSoft, fontFamily: FONTS.body }}>
          {user
            ? <>{t('login.forum.signedInPrefix')} <span style={{ color: P.oxblood, fontFamily: FONTS.display, fontWeight: 600 }}>{user.name || user.email}</span> {t('login.forum.signedInSuffix')}</>
            : t('login.forum.signInCta')}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/bookstore"
            data-testid="login-nav-bookstore"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold"
            style={{ backgroundColor: `${P.gold}33`, color: P.leather, fontFamily: FONTS.display }}
          >
            <BookOpen className="h-3.5 w-3.5" /> {t('login.forum.visitVault')}
          </Link>
          <Link
            to="/"
            data-testid="login-nav-home"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
          >
            <PenTool className="h-3.5 w-3.5" /> {t('login.forum.backToLanding')}
          </Link>
        </div>
      </div>
    </section>
  );
}
